/**
 * El panel del club, abierto desde una entrada en soporte · `endpoints.md` §A.3.
 *
 * Es **el mismo panel** que ve el dueño —el mismo marco, las mismas pantallas—,
 * con el club de la entrada en lugar del de una membresía, la banda del modo
 * soporte encima y el diálogo que confirma cada cambio. No hay una copia del panel
 * para soporte, y es a propósito: una copia acabaría enseñando algo distinto de lo
 * que ve el club, que es justo lo que se viene a mirar.
 *
 * ## La entrada se vuelve a pedir cada medio minuto
 *
 * Caduca sola a la media hora, alguien puede pulsar «Salir» en otra pestaña, y el
 * acceso al back office se puede retirar. El servidor ya lo corta todo en el acto
 * (`403`), pero la pantalla tiene que enterarse para decirlo en vez de llenarse de
 * errores: se pregunta cada treinta segundos y justo al caducar.
 */
import { useEffect, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import {
  entradaRecordada,
  fijarEntradaEnSoporte,
  olvidarEntrada,
  useEntradaEnSoporte,
  type EntradaEnSoporte,
} from '@/shared/api/soporte';
import { useYo } from '@/shared/auth/sesionContexto';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import type { SesionDeSoporte } from '@/backoffice/tipos';
import { MarcoDelPanel } from '@/club/MarcoDelPanel';

/** La entrada del servidor, como la usa el panel. */
export function aEntrada(s: SesionDeSoporte): EntradaEnSoporte {
  return {
    id: s.id,
    club: s.academy_id,
    nombre: s.academy_name,
    motivo: s.reason,
    expira: new Date(s.expires_at),
    escritura: s.writable,
    estadoDelClub: s.academy_status,
  };
}

/**
 * La que se abrió en esta pestaña, **si sigue viva y es tuya**. Otra persona del
 * equipo no abre el panel con tu entrada: el motivo que leyó el club es el tuyo.
 */
export function laEntradaViva(
  sesiones: readonly SesionDeSoporte[],
  id: string | null,
  yo: string
): SesionDeSoporte | null {
  return (
    sesiones.find((s) => s.id === id && s.staff_user_id === yo && s.active) ??
    null
  );
}

export function PanelEnSoporte() {
  const yo = useYo();
  const ir = useNavigate();
  const id = entradaRecordada();

  const sesiones = useQuery({
    queryKey: ['admin', 'soporte', 'vivas'],
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data, error } = await api.v1.admin['support-sessions'].get({
        query: { only_active: true, limit: 50 },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as SesionDeSoporte[];
    },
  });

  const sesion = sesiones.data
    ? laEntradaViva(sesiones.data, id, yo.user.id)
    : null;

  const delServidor = useMemo(
    () => (sesion ? aEntrada(sesion) : null),
    [sesion]
  );

  // El estado de módulo, que es lo que lee el `fetch` para pedir confirmación.
  useEffect(() => {
    fijarEntradaEnSoporte(delServidor);
  }, [delServidor]);

  // Fuera del marco, nada de modo soporte.
  useEffect(() => () => fijarEntradaEnSoporte(null), []);

  // Justo al caducar, se vuelve a preguntar: no se esperan los treinta segundos.
  const expira = delServidor?.expira.getTime();
  const { refetch } = sesiones;
  useEffect(() => {
    if (expira === undefined) return;
    const t = setTimeout(
      () => void refetch(),
      Math.max(0, expira - Date.now()) + 1000
    );
    return () => clearTimeout(t);
  }, [expira, refetch]);

  const terminada = Boolean(sesiones.data) && !sesion;
  useEffect(() => {
    if (terminada) olvidarEntrada();
  }, [terminada]);

  // La de la banda manda mientras el servidor no conteste: «Entrar en escritura»
  // se ve en el acto.
  const enMemoria = useEntradaEnSoporte();
  const entrada =
    enMemoria && delServidor && enMemoria.id === delServidor.id
      ? enMemoria
      : delServidor;

  if (sesiones.isPending) {
    return <Cargando filas={3} etiqueta="Abriendo el club en modo soporte" />;
  }

  if (sesiones.isError) {
    return (
      <PanelError
        problema={aProblema(sesiones.error)}
        reintentar={() => void sesiones.refetch()}
      />
    );
  }

  if (!entrada) {
    return (
      <main className="lienzo">
        <Vacio
          titulo="La entrada en soporte ya no está abierta"
          explicacion="Ha caducado a la media hora, o se ha salido de ella. Para volver a mirar el club, entra otra vez desde su ficha: el club recibirá otro aviso con el motivo."
          accion={
            <button
              type="button"
              onClick={() => void ir({ to: '/admin/soporte' })}
            >
              Volver a Soporte
            </button>
          }
        />
      </main>
    );
  }

  return <MarcoDelPanel soporte={entrada} />;
}
